import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Typography, Button, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

const AlertOverlay = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(0, 0, 0, 0.8)'
    : 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(5px)',
  padding: 20,
  isolation: 'isolate',
  overflow: 'hidden'
}));

const AlertContainer = styled(motion.div)(({ theme, severity }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
  padding: 32,
  borderRadius: 16,
  width: '90%',
  maxWidth: 420,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 10px 25px rgba(0, 0, 0, 0.5)'
    : '0 10px 25px rgba(0, 0, 0, 0.1)',
  cursor: 'default',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  border: `1px solid ${severity === 'error'
    ? theme.palette.error.main
    : severity === 'warning'
      ? theme.palette.warning.main
      : severity === 'success'
        ? theme.palette.primary.main
        : theme.palette.primary.main}`,
  '&.shake': {
    animation: 'shake 0.8s cubic-bezier(.36,.07,.19,.97) both'
  },
  '@keyframes shake': {
    '0%, 100%': { transform: 'translateX(0)' },
    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
    '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' }
  },
  [theme.breakpoints.down('sm')]: {
    padding: 24,
    gap: 16
  }
}));

const AlertTitle = styled(Typography)(({ theme, severity }) => ({
  color: severity === 'error'
    ? theme.palette.error.main
    : severity === 'warning'
      ? theme.palette.warning.main
      : severity === 'success'
        ? theme.palette.primary.main
        : theme.palette.primary.main,
  fontSize: 24,
  fontWeight: 700,
  letterSpacing: -0.5,
  marginBottom: 8,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
}));

const AlertMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '16px',
  lineHeight: 1.6,
  textAlign: 'center',
  margin: '0 auto',
  maxWidth: '100%',
  wordBreak: 'keep-all',
  whiteSpace: 'pre-line'
}));

const AlertChangesMessage = styled(Box)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '16px',
  lineHeight: 1.6,
  textAlign: 'left',
  margin: '20px auto 0',
  maxWidth: '100%',
  wordBreak: 'keep-all',
  '& strong': {
    fontWeight: 700,
    color: theme.palette.primary.main
  }
}));

const ButtonContainer = styled(Box)({
  display: 'flex',
  gap: 12,
  justifyContent: 'center',
  marginTop: 'auto',
  paddingTop: 16
});

const AlertButton = styled(Button)(({ theme, variant, severity }) => ({
  width: '100%',
  height: '48px',
  background: variant === 'contained'
    ? (severity === 'error'
      ? theme.palette.error.main
      : severity === 'warning'
        ? theme.palette.warning.main
        : severity === 'success'
          ? theme.palette.primary.main
          : theme.palette.primary.main)
    : 'transparent',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  letterSpacing: '-0.3px',
  boxShadow: variant === 'contained'
    ? (severity === 'error'
      ? '0 3px 5px 2px rgba(244, 67, 54, .3)'
      : severity === 'warning'
        ? '0 3px 5px 2px rgba(255, 152, 0, .3)'
        : severity === 'success'
          ? '0 3px 5px 2px rgba(33, 203, 243, .3)'
          : '0 3px 5px 2px rgba(33, 203, 243, .3)')
    : 'none',
  transition: 'all 0.3s ease',
  color: variant === 'contained'
    ? '#FFFFFF'
    : (severity === 'error'
      ? theme.palette.error.main
      : severity === 'warning'
        ? theme.palette.warning.main
        : severity === 'success'
          ? theme.palette.primary.main
          : theme.palette.primary.main),
  WebkitTapHighlightColor: 'transparent',

  '&:hover': {
    background: variant === 'contained'
      ? (severity === 'error'
        ? theme.palette.error.dark
        : severity === 'warning'
          ? theme.palette.warning.dark
          : severity === 'success'
            ? theme.palette.primary.dark
            : theme.palette.primary.dark)
      : theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.04)',
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained'
      ? (severity === 'error'
        ? '0 4px 10px 2px rgba(244, 67, 54, .4)'
        : severity === 'warning'
          ? '0 4px 10px 2px rgba(255, 152, 0, .4)'
          : severity === 'success'
            ? '0 4px 10px 2px rgba(33, 203, 243, .4)'
            : '0 4px 10px 2px rgba(33, 203, 243, .4)')
      : 'none'
  },
  '&:active': {
    transform: 'translateY(1px)'
  },
  '.MuiTouchRipple-root': {
    display: 'none'
  }
}));

const CustomAlert = ({
  title,
  message,
  changes,
  open,
  onClose,
  onConfirm,
  confirmText,
  cancelText,
  severity = 'info',
  isConfirm = false,
  icon,
  animate = false,
  width = 'sm',
  focusConfirmButton = false,
  additionalAction = null,
  customContent = null
}) => {
  const { t } = useTranslation();
  const confirmButtonRef = useRef(null);
  const singleButtonRef = useRef(null);
  
  // 기본값을 번역 키로 설정
  const defaultConfirmText = confirmText || t('common.confirm', '확인');
  const defaultCancelText = cancelText || t('common.cancel', '취소');

  useEffect(() => {
    if (open) {
      const elements = document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      elements.forEach(el => {
        if (el instanceof HTMLElement) {
          el.blur();
          const currentTabIndex = el.tabIndex;
          el.setAttribute('data-original-tab-index', currentTabIndex.toString());
          el.tabIndex = -1;
        }
      });

      document.activeElement instanceof HTMLElement && document.activeElement.blur();
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        if (focusConfirmButton && confirmButtonRef.current) {
          confirmButtonRef.current.focus();
        } else if (isConfirm && singleButtonRef.current) {
          singleButtonRef.current.focus();
        }
      }, 0);

      const handleKeyDown = (e) => {
        if (e.key === 'Enter' && open) {
          e.preventDefault();
          onConfirm();
        } else if (e.key === 'Escape' && open) {
          e.preventDefault();
          onClose();
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        const elements = document.querySelectorAll('[data-original-tab-index]');
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            const originalTabIndex = el.getAttribute('data-original-tab-index');
            if (originalTabIndex) {
              el.tabIndex = parseInt(originalTabIndex);
              el.removeAttribute('data-original-tab-index');
            }
          }
        });
        document.body.style.overflow = 'unset';
      };
    }
  }, [open, isConfirm, focusConfirmButton, onConfirm, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <AlertOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => {
            // overlay를 직접 클릭한 경우에만 닫기 (AlertContainer 클릭은 제외)
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose();
            }
          }}
        >
          <AlertContainer
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.5 }}
            severity={severity}
            className={severity === 'error' ? 'shake' : ''}
            style={{
              maxWidth: width === 'md' ? '600px' : '420px',
              width: '90%'
            }}
            onClick={(e) => {
              // AlertContainer 내부 클릭은 전파 방지
              e.stopPropagation();
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <AlertTitle variant="h6" severity={severity}>
                {title}
                {icon && (
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.5 }}>
                    {icon}
                  </motion.div>
                )}
              </AlertTitle>
              <AlertMessage>
                {message}
              </AlertMessage>
              {changes && (
                <AlertChangesMessage>
                  <ReactMarkdown>{changes}</ReactMarkdown>
                </AlertChangesMessage>
              )}
              {customContent && (
                <Box sx={{ mt: 2 }}>
                  {customContent}
                </Box>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <ButtonContainer>
                {!isConfirm && onClose && (
                  <AlertButton
                    variant="outlined"
                    onClick={onClose}
                    severity={severity}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {defaultCancelText}
                  </AlertButton>
                )}
                {onConfirm && (
                  <AlertButton
                    ref={!isConfirm ? confirmButtonRef : singleButtonRef}
                    variant="contained"
                    onClick={onConfirm}
                    severity={severity}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {defaultConfirmText}
                  </AlertButton>
                )}
                {additionalAction && (
                  <AlertButton
                    variant="outlined"
                    onClick={additionalAction.action}
                    severity={severity}
                    component={motion.button}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {additionalAction.text}
                  </AlertButton>
                )}
              </ButtonContainer>
            </motion.div>
          </AlertContainer>
        </AlertOverlay>
      )}
    </AnimatePresence>
  );
};

export default React.memo(CustomAlert);